import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Video as Youtube, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Link2, 
  Sparkles, 
  Check, 
  Info,
  Film,
  Music,
  Tv,
  Zap
} from 'lucide-react';

export const YoutubeVideoDownloader = () => {
  const { withAd, toast } = useAdRedirect();
  const [videoUrl, setVideoUrl] = useState('');
  const [videoId, setVideoId] = useState('');
  const [videoProgress, setVideoProgress] = useState({ key: '', progress: 0, step: '' });

  // Parse YouTube Video ID from any input URL
  const extractVideoId = (url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return match[2];
    }
    if (url.trim().length === 11 && !url.includes('/') && !url.includes('.')) {
      return url.trim();
    }
    return '';
  };

  const handleUrlChange = (e) => {
    const input = e.target.value;
    setVideoUrl(input);
    const parsedId = extractVideoId(input);
    setVideoId(parsedId);
  };

  useEffect(() => {
    // Default pleasant demo video ID
    setVideoUrl('https://www.youtube.com/watch?v=jfKfPfyJRdk');
    setVideoId('jfKfPfyJRdk');
  }, []);

  const videoOptions = [
    {
      key: '1080p',
      name: '1080p Full HD Video',
      format: 'MP4',
      type: 'video',
      fps: '60fps',
      audio: 'Included',
      badge: 'Ultra Crisp',
      color: '#ef4444',
      icon: Tv
    },
    {
      key: '720p',
      name: '720p HD Quality',
      format: 'MP4',
      type: 'video',
      fps: '30fps',
      audio: 'Included',
      badge: 'High Quality',
      color: 'var(--primary)',
      icon: Film
    },
    {
      key: '480p',
      name: '480p Standard Quality',
      format: 'MP4',
      type: 'video',
      fps: '30fps',
      audio: 'Included',
      badge: 'Standard Size',
      color: '#10b981',
      icon: Zap
    },
    {
      key: 'mp3',
      name: 'MP3 High-Fidelity Audio',
      format: 'MP3',
      type: 'audio',
      bitrate: '320 kbps',
      badge: 'Audio Only',
      color: '#8b5cf6',
      icon: Music
    }
  ];

  const handleDownloadVideo = (optKey) => {
    if (videoProgress.key) return;

    setVideoProgress({ key: optKey, progress: 15, step: 'Connecting to secure stream proxy...' });

    setTimeout(() => {
      setVideoProgress(prev => ({ ...prev, progress: 45, step: 'Bypassing cipher stream signature...' }));
    }, 400);

    setTimeout(() => {
      setVideoProgress(prev => ({ ...prev, progress: 80, step: 'Transcoding media container format...' }));
    }, 850);

    setTimeout(() => {
      setVideoProgress(prev => ({ ...prev, progress: 100, step: 'Complete! Initializing download...' }));
      
      setTimeout(() => {
        setVideoProgress({ key: '', progress: 0, step: '' });
        window.open(`https://www.y2mate.com/youtube/${videoId}`, '_blank');
      }, 600);
    }, 1300);
  };

  const handleReset = () => {
    setVideoUrl('');
    setVideoId('');
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Free YouTube Video Downloader - Download MP4 & MP3" 
        description="Download YouTube videos in MP4 format (1080p Full HD, 720p) and MP3 audio streams instantly. Fast, secure, and 100% free online video downloader."
        keywords="youtube video downloader, download youtube mp4, youtube to mp4, download youtube audio, free youtube downloader online, grab youtube video"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>YouTube Video Downloader</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          YouTube Video & Audio Downloader
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Download high-quality YouTube MP4 videos or extract crystal-clear MP3 audio streams instantly.
        </p>
      </div>

      {/* Input Section */}
      <div style={{
        background: '#ffffff',
        border: '1px solid var(--border-color)',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: 'var(--shadow-sm)',
        marginBottom: '32px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Link2 size={18} style={{ color: '#ef4444' }} />
          <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Paste YouTube Video Link</h3>
        </div>

        <div style={{ display: 'flex', gap: '12px' }} className="input-row-downloader">
          <input 
            type="text"
            value={videoUrl}
            onChange={handleUrlChange}
            placeholder="E.G. https://www.youtube.com/watch?v=dQw4w9WgXcQ"
            style={{
              flex: 1,
              padding: '14px 18px',
              borderRadius: '12px',
              border: '1px solid var(--border-color)',
              fontSize: '1rem',
              fontWeight: 600,
              outline: 'none',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              boxSizing: 'border-box'
            }}
          />
          {videoUrl && (
            <button
              onClick={handleReset}
              style={{
                background: 'rgba(15, 23, 42, 0.03)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '12px',
                padding: '0 20px',
                fontSize: '0.9rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'var(--transition-smooth)'
              }}
              className="reset-btn"
            >
              Clear
            </button>
          )}
        </div>

        {videoUrl && !videoId && (
          <p style={{ margin: '8px 0 0 0', fontSize: '0.8rem', color: '#ef4444', fontWeight: 600 }}>
            Could not parse a valid YouTube video ID. Please check your link format.
          </p>
        )}
      </div>

      {/* Media Options */}
      {videoId ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          
          {/* Main big preview */}
          <div style={{
            background: '#ffffff',
            border: '1px solid var(--border-color)',
            borderRadius: '20px',
            padding: '24px',
            boxShadow: 'var(--shadow-sm)'
          }}>
            <span style={{
              fontSize: '0.8rem',
              fontWeight: 800,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              display: 'block',
              marginBottom: '16px'
            }}>
              Active Video ID: <strong style={{ color: '#ef4444', fontFamily: 'monospace' }}>{videoId}</strong>
            </span>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '24px',
              alignItems: 'center'
            }} className="preview-grid-cover">
              
              <div style={{
                borderRadius: '14px',
                overflow: 'hidden',
                aspectRatio: '16/10',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '680px'
              }}>
                <img 
                  src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} 
                  onError={(e) => {
                    e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                  }}
                  alt="YouTube Video Cover" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <h4 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                  Video & Audio Streams Ready
                </h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5, maxWidth: '420px' }}>
                  Select your desired resolution container below. Our engine instantly connects to secure high-speed transcoding proxy servers to deliver direct MP4 or MP3 files.
                </p>
                <Link 
                  to="/youtube-downloader" 
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: 'var(--primary)',
                    textDecoration: 'none',
                    marginTop: '8px'
                  }}
                >
                  👉 Looking for YouTube Thumbnails? Click here
                </Link>
              </div>

            </div>
          </div>

          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '20px' }}>
              Available Media Streams
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '24px'
            }}>
              {videoOptions.map((opt) => {
                const IconComp = opt.icon;
                const isCurProcessing = videoProgress.key === opt.key;
                
                return (
                  <div
                    key={opt.key}
                    style={{
                      background: '#ffffff',
                      border: '1px solid var(--border-color)',
                      borderRadius: '16px',
                      padding: '24px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      boxShadow: 'var(--shadow-xs)',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="resolution-card"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '12px',
                          background: `rgba(${opt.color === '#ef4444' ? '239, 68, 68' : opt.color === '#8b5cf6' ? '139, 92, 246' : '16, 185, 129'}, 0.1)`,
                          color: opt.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <IconComp size={22} />
                        </div>
                        <div>
                          <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)' }}>{opt.name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Format: <strong>{opt.format}</strong></div>
                        </div>
                      </div>

                      <span style={{
                        background: opt.color === '#ef4444' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(15, 23, 42, 0.04)',
                        color: opt.color === '#ef4444' ? '#ef4444' : 'var(--text-secondary)',
                        fontSize: '0.65rem',
                        fontWeight: 800,
                        padding: '3px 8px',
                        borderRadius: '6px'
                      }}>
                        {opt.badge}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      background: 'var(--bg-secondary)',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      color: 'var(--text-secondary)'
                    }}>
                      {opt.type === 'video' ? (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Frame Rate:</span> <strong>{opt.fps}</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Audio Channel:</span> <strong style={{ color: '#10b981' }}>{opt.audio}</strong>
                          </div>
                        </>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Audio Bitrate:</span> <strong style={{ color: '#8b5cf6' }}>{opt.bitrate}</strong>
                        </div>
                      )}
                    </div>

                    {isCurProcessing && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary)' }}>
                          <span>{videoProgress.step}</span>
                          <span>{videoProgress.progress}%</span>
                        </div>
                        <div style={{ height: '6px', background: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${videoProgress.progress}%`, background: 'var(--primary)', transition: 'width 0.3s ease' }} />
                        </div>
                      </div>
                    )}

                    <AdToast message={toast} />
                    <button
                      onClick={withAd(`yt-vid-${opt.key}`, () => handleDownloadVideo(opt.key))}
                      disabled={videoProgress.key !== ''}
                      style={{
                        marginTop: 'auto',
                        background: opt.color === '#ef4444' ? 'linear-gradient(90deg, #ef4444 0%, var(--primary) 100%)' : '#ffffff',
                        color: opt.color === '#ef4444' ? '#ffffff' : 'var(--text-primary)',
                        border: opt.color === '#ef4444' ? 'none' : '1px solid var(--border-color)',
                        borderRadius: '12px',
                        padding: '12px',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        cursor: videoProgress.key ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'var(--transition-smooth)'
                      }}
                      className={opt.color === '#ef4444' ? 'download-max-btn' : 'download-standard-btn'}
                    >
                      {isCurProcessing ? <RefreshCw size={16} className="spinner" /> : <Download size={16} />}
                      {isCurProcessing ? 'Processing Stream...' : `Download ${opt.format}`}
                    </button>

                  </div>
                );
              })}
            </div>
          </div>

        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-secondary)',
          background: '#ffffff',
          borderRadius: '20px',
          border: '1px solid var(--border-color)'
        }}>
          Please paste a YouTube video link inside the text box above to load video download streams.
        </div>
      )}

      <style>{`
        @media (min-width: 768px) {
          .preview-grid-cover {
            grid-template-columns: 1.5fr 1fr !important;
          }
        }
        .reset-btn:hover { background: rgba(15, 23, 42, 0.06) !important; }
        .download-max-btn:hover { transform: translateY(-1px); opacity: 0.95; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2) !important; }
        .download-standard-btn:hover { background: var(--bg-secondary) !important; transform: translateY(-1px); }
        .resolution-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-sm) !important; border-color: rgba(239, 68, 68, 0.15) !important; }
        .spinner { animation: spin 1s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="youtube-video-downloader" />
    </div>
  );
};

export default YoutubeVideoDownloader;
